import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// Pure model for the Hotels desk — the remainder-first accommodation view. ONE
// classifier decides every PNR's lane so the goal bar, the work queue and the
// "done" strip can never disagree. The operator's goal ends the shift: every
// hotel-needing PNR is CONFIRMED, or resolved without a hotel (self-managed /
// no-offer declared), or the queue says exactly why it still isn't.

export type DeskLane =
  /** Hotel booked and confirmed — done. */
  | 'CONFIRMED'
  /** Offer HELD, waiting on the passenger's reply (acceptance gate). */
  | 'AWAITING_REPLY'
  /** Needs a hotel and has no live reservation — the operator's queue. */
  | 'NEEDS_HOTEL'
  /** Passenger declined the offer / the hold expired — needs an alternative. */
  | 'DECLINED'
  /** Resolved WITHOUT a hotel: voucher/self-managed or a declared no-offer. */
  | 'RESOLVED_WITHOUT'
  /** No overnight need (short gap / same-day) — not on this desk. */
  | 'NOT_NEEDED'
  /** No accommodation signal yet AND rebooking hasn't decided the group — the
   *  lodging need is UNKNOWN, not absent. Blocks the desk's all-green. */
  | 'UNASSESSED';

export interface DeskRow {
  row: DisruptionPnrGroupRow;
  lane: DeskLane;
}

export interface DeskState {
  rows: DeskRow[];
  /** Queue lanes only (NEEDS_HOTEL + DECLINED + AWAITING_REPLY), pax-weighted worst first. */
  queue: DeskRow[];
  counts: Record<DeskLane, { pnrs: number; pax: number }>;
  /** PNRs the goal covers (everything except NOT_NEEDED and UNASSESSED). */
  goalPnrs: number;
  goalPax: number;
  /** True when nothing is left in NEEDS_HOTEL/DECLINED/AWAITING_REPLY AND every
   *  row has actually been assessed (no UNASSESSED) — an unassessed roster must
   *  never read as "everyone accommodated". */
  allSettled: boolean;
}

/** Mirrors operator-bff allocation.service `isRebookingResolved`: the engine /
 *  operator has DECIDED the group, so the accommodation denorm is meaningful. */
function isRebookingResolved(status?: string, noOptionsReason?: string): boolean {
  // NO_OPTIONS because every option LEFT (`OPTIONS_DEPARTED`) is a search away — undecided.
  if (status === 'NO_OPTIONS') return noOptionsReason !== 'OPTIONS_DEPARTED';
  return status === 'SELECTED' || status === 'OVERRIDDEN' || status === 'NOT_APPLICABLE';
}

/**
 * Does this PNR need an overnight hotel at all? (cases denorm is the seam.)
 *
 * One of the UI copies of the domain's §12 gate (`cases:needsHotelAllocation@v2`,
 * flight-delay S4); `desk-model.hotel-rule.test.ts` runs the domain's decision table
 * against it and pins every divergence by name. Order:
 *   1. `accommodationKind` first (the classifier's verdict, written with
 *      `requiresHotel` — invariant `requiresHotel === (kind === 'HOTEL')`): HOTEL → yes,
 *      MEAL_VOUCHER / NONE → no. The domain ignores the kind; with the invariant both
 *      agree, and the stay-home declaration writes NONE, so suppression lands here.
 *   2. NOT_APPLICABLE → the stamp's own verdict, EXACTLY as the domain (flight-delay U1):
 *      only an explicit `requiresHotel: false` withholds the room (the delay's meals-only
 *      stamp, stay-home); `true` (the delay-hotel stamp — the policy's next-day rule,
 *      `FLIGHT_NEXT_DAY` / HOTEL, delay-limits — or care-only) or ABSENT (the
 *      POLICY_DISABLED reclassification, legacy rows) need one. A delay is not "never a
 *      hotel": its stamp says which. The old rule ("absent ⇒ no, unless POLICY_DISABLED")
 *      is gone — POLICY_DISABLED is now just one absent case.
 *   3. Otherwise `requiresHotel === true` — pre-existing desk semantics, unchanged: a
 *      group whose need is not stamped yet is UNASSESSED (see `laneOf`), not a room.
 */
export function needsHotel(row: DisruptionPnrGroupRow): boolean {
  if (row.accommodationKind === 'HOTEL') return true;
  if (row.accommodationKind === 'MEAL_VOUCHER' || row.accommodationKind === 'NONE') return false;
  if (row.rebookingStatus === 'NOT_APPLICABLE') return row.requiresHotel !== false;
  return row.requiresHotel === true;
}

export function laneOf(row: DisruptionPnrGroupRow): DeskLane {
  // A declared outcome ends the PNR's accommodation story regardless of the rest.
  if (row.accommodationOutcome) return 'RESOLVED_WITHOUT';
  // A meal voucher INSTEAD of a hotel resolves the group on this desk (Nexa decided its
  // flight and the gap earns a meal, not a room) — it counts toward the desk's goal.
  // NOT on a NOT_APPLICABLE group: there it is the flight-delay MEALS-ONLY stamp — the
  // passenger keeps their own flight, the meal is a delay-care entitlement (wallet /
  // care desk), and no room was owed. That group lanes NOT_NEEDED (via `needsHotel` →
  // false), out of the goal, like any same-day group (flight-delay U1); a delay-hotel
  // group (HOTEL) is the desk's like any other.
  if (row.accommodationKind === 'MEAL_VOUCHER' && row.rebookingStatus !== 'NOT_APPLICABLE') {
    return 'RESOLVED_WITHOUT';
  }
  // Pre-rebooking there is NO accommodation signal at all — `requiresHotel` is
  // undefined, not false. Laning that row NOT_NEEDED made an untouched roster
  // read all-green; the need is UNKNOWN until rebooking decides the group.
  if (
    !row.accommodationOutcome &&
    !row.accommodationKind &&
    row.requiresHotel === undefined &&
    !row.hotelReservationUrn &&
    !isRebookingResolved(row.rebookingStatus, row.noOptionsReason)
  ) {
    return 'UNASSESSED';
  }
  if (!needsHotel(row)) return 'NOT_NEEDED';
  if (row.hotelReservationUrn) {
    return row.hotelReservationStatus === 'HELD' ? 'AWAITING_REPLY' : 'CONFIRMED';
  }
  if (row.subCaseStatus === 'DECLINED') return 'DECLINED';
  return 'NEEDS_HOTEL';
}

const QUEUE_ORDER: Partial<Record<DeskLane, number>> = {
  NEEDS_HOTEL: 0,
  DECLINED: 1,
  AWAITING_REPLY: 2,
};

export function buildDesk(rows: DisruptionPnrGroupRow[]): DeskState {
  const deskRows: DeskRow[] = rows.map((row) => ({ row, lane: laneOf(row) }));
  const counts: DeskState['counts'] = {
    CONFIRMED: { pnrs: 0, pax: 0 },
    AWAITING_REPLY: { pnrs: 0, pax: 0 },
    NEEDS_HOTEL: { pnrs: 0, pax: 0 },
    DECLINED: { pnrs: 0, pax: 0 },
    RESOLVED_WITHOUT: { pnrs: 0, pax: 0 },
    NOT_NEEDED: { pnrs: 0, pax: 0 },
    UNASSESSED: { pnrs: 0, pax: 0 },
  };
  for (const d of deskRows) {
    counts[d.lane].pnrs += 1;
    counts[d.lane].pax += d.row.paxCount ?? 0;
  }
  const queue = deskRows
    .filter((d) => d.lane in QUEUE_ORDER)
    .sort(
      (a, b) =>
        QUEUE_ORDER[a.lane]! - QUEUE_ORDER[b.lane]! ||
        (b.row.paxCount ?? 0) - (a.row.paxCount ?? 0) ||
        a.row.pnrLabel.localeCompare(b.row.pnrLabel),
    );
  const goalPnrs = deskRows.length - counts.NOT_NEEDED.pnrs - counts.UNASSESSED.pnrs;
  const goalPax =
    deskRows.reduce((s, d) => s + (d.row.paxCount ?? 0), 0) -
    counts.NOT_NEEDED.pax -
    counts.UNASSESSED.pax;
  return {
    rows: deskRows,
    queue,
    counts,
    goalPnrs,
    goalPax,
    allSettled:
      deskRows.length > 0 &&
      counts.NEEDS_HOTEL.pnrs === 0 &&
      counts.DECLINED.pnrs === 0 &&
      counts.AWAITING_REPLY.pnrs === 0 &&
      counts.UNASSESSED.pnrs === 0,
  };
}

/**
 * The reason line per queue lane (the row's "why am I here") as an i18n KEY of
 * the `tower` namespace — this file holds keys, never language. The rendering
 * component calls `t()` on it; an empty string means "no reason to show".
 */
export function laneReason(d: DeskRow): string {
  switch (d.lane) {
    case 'NEEDS_HOTEL':
      return 'desk.lane.needsHotel';
    case 'DECLINED':
      return 'desk.lane.declined';
    case 'AWAITING_REPLY':
      return 'desk.lane.awaitingReply';
    default:
      return '';
  }
}
